import { CheckCircleIcon } from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { Field } from '@/modules/primitives3D/editorShell';
import { FieldLabel, FlowTitle } from '@/components/ui/text';
import { TemplateSceneCard } from '@/components/dashboard/template-scene-card';
import projectPlaceholder from '@/assets/project-historical.jpg';
import { templateFeatureLabel } from '../templateOptions';
import type { TemplateScene } from '@/types/cmsTypes';
import type { TemplateDifficulty } from '../types';

interface Props {
  storyName: string;
  creatorName: string;
  description: string;
  learnings: string;
  category: string;
  difficulty: TemplateDifficulty | '';
  tags: string[];
  features: string[];
  coverImageUrl?: string | null;
  submitted: boolean;
  isResubmission: boolean;
}

export function TemplateConfirmStep({
  storyName,
  creatorName,
  description,
  learnings,
  category,
  difficulty,
  tags,
  features,
  coverImageUrl,
  submitted,
  isResubmission,
}: Props) {
  if (submitted) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-lureo-brand text-lureo-on-brand">
            <CheckCircleIcon className="h-8 w-8" />
          </div>
          <FlowTitle as="h3" className="mt-4">
            {isResubmission ? 'Template resubmitted' : 'Template submitted'}
          </FlowTitle>
          <p className="mt-3 font-lureo-font-prose text-lureo-15 text-lureo-ink-subtle leading-normal">
            We'll review and let you know within 7 days, via notification and email. Your original
            story stays editable in the meantime.
          </p>
        </div>
      </div>
    );
  }

  const preview: TemplateScene = {
    id: 'preview',
    title: storyName || 'Untitled template',
    description: description || 'No description available.',
    heroImage: coverImageUrl || projectPlaceholder,
    category,
    tags,
    features,
    duration: 0,
    difficulty: (difficulty || 'beginner') as TemplateDifficulty,
  };

  return (
    <div className="h-full grid grid-cols-2 divide-x-lureo-border-width divide-lureo-line">
      <div className="min-w-0 pr-7 overflow-y-auto">
        <Card>
          <Field>
            <FieldLabel mb>Template</FieldLabel>
            <p className="font-lureo-font-ui font-medium text-lureo-14 text-lureo-ink leading-[1.3]">
              {storyName || '—'}
            </p>
          </Field>
          <Field>
            <FieldLabel mb>Creator</FieldLabel>
            <p className="font-lureo-font-ui font-medium text-lureo-14 text-lureo-ink leading-[1.3]">
              {creatorName || '—'}
            </p>
          </Field>
          <Field>
            <FieldLabel mb>Listed as</FieldLabel>
            <div className="flex flex-wrap gap-2">
              {[category, difficulty ? DIFFICULTY_LABELS[difficulty] : '', ...tags]
                .filter(Boolean)
                .map((chip, i) => (
                  <span
                    key={`${chip}-${i}`}
                    className="inline-flex items-center rounded-lureo-radius-pill bg-lureo-brand-ink/10 border-lureo-border-width-medium border-lureo-brand-ink text-lureo-brand-ink py-1 px-3 font-lureo-font-ui text-lureo-12 font-medium leading-[1.3]"
                  >
                    {chip}
                  </span>
                ))}
            </div>
          </Field>
          <Field>
            <FieldLabel mb>Features it shows off</FieldLabel>
            <div className="flex flex-wrap gap-2">
              {features.length === 0 ? (
                <p className="font-lureo-font-prose text-lureo-14 text-lureo-ink-subtle leading-[1.4]">None ticked.</p>
              ) : (
                features.map((key) => (
                  <span
                    key={key}
                    className="inline-flex items-center rounded-lureo-radius-pill bg-lureo-surface border-lureo-border-width-medium border-lureo-line-strong text-lureo-ink-subtle py-1 px-3 font-lureo-font-ui text-lureo-12 font-medium leading-[1.3]"
                  >
                    {templateFeatureLabel(key)}
                  </span>
                ))
              )}
            </div>
          </Field>
          <Field className="!mb-0">
            <FieldLabel mb>What creators will learn</FieldLabel>
            <p className="font-lureo-font-prose text-lureo-14 text-lureo-ink-subtle leading-normal whitespace-pre-line">
              {learnings || '—'}
            </p>
          </Field>
        </Card>

        <p className="mt-4 font-lureo-font-prose text-lureo-14 text-lureo-ink-subtle leading-normal">
          We'll review and let you know within 7 days via notification and email. Your original story
          stays editable: the template is a locked copy of this version.
        </p>
      </div>

      <div className="min-w-0 pl-7 flex flex-col">
        <FieldLabel className="mb-3">How it appears in the gallery</FieldLabel>
        <div className="pointer-events-none max-w-80">
          <TemplateSceneCard template={preview} onSelect={() => {}} />
        </div>
      </div>
    </div>
  );
}

const DIFFICULTY_LABELS: Record<TemplateDifficulty, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};
